import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  FileText,
  Image as ImageIcon,
  X,
  Clock,
  Calendar,
  Sparkles,
} from "lucide-react";
import Button from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { RichTextEditor } from "../../components/common/RichTextEditor";
import { MediaPickerModal } from "../../components/common/MediaPickerModal";
import { MediaAsset } from "../../services/mediaService";
import { useCategoriesQuery, useCreatePostMutation } from "../../hooks/queries/useBlogQueries";
import { CreateBlogPostPayload } from "../../types/blog";
import { useToast } from "../../hooks/otherHooks/useToast";

const SuperAdminCreateBlog: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: categories = [] } = useCategoriesQuery();
  const createPostMutation = useCreatePostMutation();

  // Form Fields
  const [title, setTitle] = useState<string>("");
  const [postSlug, setPostSlug] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [categoryId, setCategoryId] = useState<number | "">("");
  const [excerpt, setExcerpt] = useState<string>("");
  const [readTime, setReadTime] = useState<number>(5);
  const [pubDate, setPubDate] = useState<string>(() => {
    const now = new Date();
    const tzOffset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffset).toISOString().slice(0, 16);
  });

  const [featuredMediaId, setFeaturedMediaId] = useState<number | "">("");
  const [selectedMediaAsset, setSelectedMediaAsset] = useState<MediaAsset | null>(null);
  const [featuredImageUrl, setFeaturedImageUrl] = useState<string>("");
  const [featuredImageCaption, setFeaturedImageCaption] = useState<string>("");
  const [isMediaModalOpen, setIsMediaModalOpen] = useState<boolean>(false);

  // Helper to generate slug from text
  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/[\s_-]+/g, "-")
      .replace(/^-+|-+$/g, "");
  };

  useEffect(() => {
    if (categories.length > 0 && categoryId === "") {
      setCategoryId(categories[0].id);
    }
  }, [categories, categoryId]);

  const handleSelectMedia = (asset: MediaAsset) => {
    setFeaturedMediaId(asset.id);
    setSelectedMediaAsset(asset);
    const assetUrl = asset.url || asset.external_url || "";
    setFeaturedImageUrl(assetUrl);
    if (asset.caption || asset.title) {
      setFeaturedImageCaption(asset.caption || asset.title);
    }
    setIsMediaModalOpen(false);
    addToast({
      message: `Featured media selected: "${asset.title}"`,
      bg: "success",
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !categoryId) {
      addToast({
        message: "Please fill in Title, Category, and Content.",
        bg: "danger",
      });
      return;
    }

    const computedSlug = postSlug.trim() || generateSlug(title.trim());
    const payload: CreateBlogPostPayload = {
      title: title.trim(),
      slug: computedSlug,
      content: content.trim(),
      category: Number(categoryId),
      pub_date: pubDate ? new Date(pubDate).toISOString() : undefined,
      excerpt: excerpt.trim() || undefined,
      read_time: Number(readTime),
      featured_media_id: featuredMediaId ? Number(featuredMediaId) : null,
      featured_image_url: featuredImageUrl.trim() || undefined,
      featured_image_url_caption: featuredImageCaption.trim() || undefined,
      image: featuredImageUrl.trim() || undefined,
      image_caption: featuredImageCaption.trim() || undefined,
    };

    createPostMutation.mutate(payload, {
      onSuccess: (newPost) => {
        addToast({
          message: `Article "${newPost.title}" published successfully!`,
          bg: "success",
        });
        navigate("/superadmin/dashboard/blog");
      },
      onError: (err: any) => {
        console.error("Create article failed:", err);
        const errorMsg =
          err.response?.data && typeof err.response.data === "object"
            ? Object.entries(err.response.data)
                .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : JSON.stringify(v)}`)
                .join(" | ")
            : err.message || "Failed to publish article.";
        addToast({ message: errorMsg, bg: "danger" });
      },
    });
  };

  return (
    <div className="cht-max-w-4xl cht-mx-auto cht-space-y-6 cht-pb-16 cht-animate-in cht-fade-in cht-duration-700">
      {/* Header Bar */}
      <div className="cht-flex cht-items-center cht-justify-between">
        <Link
          to="/superadmin/dashboard/blog"
          className="cht-inline-flex cht-items-center cht-space-x-2 cht-text-xs cht-font-black cht-text-gray-500 hover:cht-text-[#3e57da] cht-transition-colors"
        >
          <ArrowLeft className="cht-h-4 cht-w-4" />
          <span>Back to Articles</span>
        </Link>
      </div>

      <div className="cht-rounded-[32px] cht-bg-white cht-border cht-border-gray-100 cht-p-6 sm:cht-p-8 cht-shadow-sm cht-space-y-6">
        <div className="cht-flex cht-items-center cht-space-x-4 cht-border-b cht-border-gray-100 cht-pb-5">
          <div className="cht-h-12 cht-w-12 cht-rounded-2xl cht-bg-indigo-50 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
            <FileText className="cht-h-6 cht-w-6" />
          </div>
          <div>
            <h1 className="cht-text-xl cht-font-black cht-text-[#1e293b]">
              Compose New Article
            </h1>
            <p className="cht-text-xs cht-text-gray-500 cht-font-medium">
              Create platform publications, editorial thought leadership, and technical releases.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="cht-space-y-6">
          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
            <div>
              <Input
                label="Article Title *"
                type="text"
                placeholder="e.g. Scaling Next-Gen Architectures in 2026"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!postSlug) setPostSlug(generateSlug(e.target.value));
                }}
                required
              />
            </div>

            <div>
              <Input
                label="URL Slug *"
                type="text"
                placeholder="e.g. scaling-next-gen-architectures-2026"
                value={postSlug}
                onChange={(e) => setPostSlug(e.target.value)}
                inputClassName="cht-font-mono"
                required
              />
            </div>
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
            <div className="cht-space-y-1.5">
              <label className="cht-text-xs cht-font-black cht-text-gray-700 cht-block">Category *</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(Number(e.target.value))}
                className="cht-w-full cht-h-11 cht-rounded-xl cht-bg-gray-50 cht-border cht-border-gray-200/80 cht-px-3.5 cht-text-xs cht-font-bold cht-text-gray-800 focus:cht-bg-white focus:cht-border-[#3e57da] cht-outline-none"
                required
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Input
                label="Read Time (min)"
                type="number"
                min={1}
                value={readTime}
                onChange={(e) => setReadTime(Number(e.target.value))}
                leftIcon={<Clock className="cht-h-4 cht-w-4 cht-text-gray-400" />}
              />
            </div>
          </div>

          <div>
            <Input
              multiline
              rows={2}
              label="Excerpt / Brief Summary"
              placeholder="Brief summary displayed on card previews and search metadata..."
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
            />
          </div>

          {/* Full WYSIWYG Content Body using RichTextEditor */}
          <div className="cht-space-y-1.5">
            <label className="cht-text-xs cht-font-black cht-text-gray-700 cht-block">
              Article Content Body *
            </label>
            <RichTextEditor
              value={content}
              onChange={(val) => setContent(val)}
              placeholder="Write full article body text with headings, images, code blocks, lists, and formatting..."
              minHeight="420px"
            />
          </div>

          {/* Featured Media Asset Selection */}
          <div className="cht-space-y-2">
            <label className="cht-text-xs cht-font-black cht-text-gray-700 cht-block">
              Featured Cover Image
            </label>
            {selectedMediaAsset || featuredMediaId ? (
              <div className="cht-flex cht-items-center cht-justify-between cht-p-4 cht-rounded-2xl cht-bg-gray-50 cht-border cht-border-gray-200 cht-text-xs">
                <div className="cht-flex cht-items-center cht-space-x-3 cht-min-w-0">
                  {selectedMediaAsset?.url || featuredImageUrl ? (
                    <img
                      src={selectedMediaAsset?.url || featuredImageUrl}
                      alt={selectedMediaAsset?.title || "Featured Media"}
                      className="cht-h-12 cht-w-16 cht-rounded-xl cht-object-cover cht-border cht-border-gray-200 cht-shrink-0"
                    />
                  ) : (
                    <div className="cht-h-12 cht-w-12 cht-rounded-xl cht-bg-indigo-50 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0 cht-font-bold">
                      <ImageIcon className="cht-h-6 cht-w-6" />
                    </div>
                  )}
                  <div className="cht-min-w-0">
                    <span className="cht-font-black cht-text-[#1e293b] cht-block cht-truncate">
                      {selectedMediaAsset?.title || `Media Asset #${featuredMediaId}`}
                    </span>
                    <span className="cht-text-[10px] cht-text-gray-400 cht-font-mono">
                      {selectedMediaAsset?.media_type
                        ? `[${selectedMediaAsset.media_type.toUpperCase()}]`
                        : "Selected Asset"}
                    </span>
                  </div>
                </div>

                <div className="cht-flex cht-items-center cht-space-x-2 cht-shrink-0">
                  <Button
                    type="button"
                    onClick={() => setIsMediaModalOpen(true)}
                    size="sm"
                    variant="outline"
                    className="cht-text-xs cht-font-bold cht-rounded-xl"
                  >
                    Change Media
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      setFeaturedMediaId("");
                      setSelectedMediaAsset(null);
                      setFeaturedImageUrl("");
                      setFeaturedImageCaption("");
                    }}
                    size="sm"
                    variant="ghost"
                    className="cht-text-xs cht-font-bold cht-rounded-xl cht-text-rose-600 hover:cht-bg-rose-50"
                  >
                    <X className="cht-h-4 cht-w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                onClick={() => setIsMediaModalOpen(true)}
                variant="outline"
                leftIcon={<ImageIcon className="cht-h-4 cht-w-4 cht-text-[#3e57da]" />}
                className="cht-w-full cht-h-12 cht-rounded-xl cht-bg-gray-50 cht-border-dashed cht-border-gray-300 hover:cht-bg-white cht-text-xs cht-font-black cht-text-gray-700"
              >
                Select Cover Image from Media Library
              </Button>
            )}
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
            <div>
              <Input
                label="Direct Image URL (External Fallback)"
                type="url"
                placeholder="https://images.unsplash.com/..."
                value={featuredImageUrl}
                onChange={(e) => setFeaturedImageUrl(e.target.value)}
              />
            </div>

            <div>
              <Input
                label="Image Caption / Attribution"
                type="text"
                placeholder="Photo caption text..."
                value={featuredImageCaption}
                onChange={(e) => setFeaturedImageCaption(e.target.value)}
              />
            </div>
          </div>

          <div className="cht-grid cht-grid-cols-1 sm:cht-grid-cols-2 cht-gap-4">
            <div>
              <Input
                label="Publication Date & Time"
                type="datetime-local"
                value={pubDate}
                onChange={(e) => setPubDate(e.target.value)}
                leftIcon={<Calendar className="cht-h-4 cht-w-4 cht-text-gray-400" />}
              />
            </div>
          </div>

          <div className="cht-pt-6 cht-border-t cht-border-gray-100 cht-flex cht-items-center cht-justify-end cht-space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/superadmin/dashboard/blog")}
              className="cht-rounded-xl cht-text-xs cht-font-bold"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={createPostMutation.isPending}
              isLoading={createPostMutation.isPending}
              className="cht-rounded-xl cht-text-xs cht-font-black cht-px-8"
            >
              {createPostMutation.isPending ? "Publishing..." : "Publish Article"}
            </Button>
          </div>
        </form>
      </div>

      {/* Media Library Modal */}
      <MediaPickerModal
        isOpen={isMediaModalOpen}
        onClose={() => setIsMediaModalOpen(false)}
        onSelect={handleSelectMedia}
        title="Select Featured Article Image from Media Library"
        allowedTypes={["image"]}
      />
    </div>
  );
};

export default SuperAdminCreateBlog;
